import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Hourglass } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatDate, formatToken } from '../lib/format';
import type { EngineParameters } from '../lib/lookup-types';
import { atomicToDecimal } from '../state/AppDataContext';

/*
 * Owner 2026-10-06: the member must be reminded about a held P3 - a banner while the holding waits for P4, and a popup
 * once the dormancy period (3 years; testnet minutes) has passed, with the exact forfeiture date (dormancy + reclaim).
 * Owner: during the reclaim year the popup returns every quarter (reclaim period / 4) even if it was dismissed.
 */
interface Ledger { upgradeEscrowAtomic: string; reserveAtomic: string; dormancy: { lastActivityAt: string | null; dormantSince: string | null } | null }

export function DormancyReminder({ bdId }: { bdId: string }) {
  const [ledger, setLedger] = useState<Ledger | null>(null);
  const [windows, setWindows] = useState<{ dormancySeconds: number; reclaimSeconds: number } | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api<{ ledger: Ledger | null }>(`/api/public/onchain/${encodeURIComponent(bdId)}`).then((data) => { if (!cancelled) setLedger(data.ledger); }).catch(() => undefined);
    api<EngineParameters>('/api/public/onchain/params').then((params) => { if (!cancelled && params.dormancy) setWindows(params.dormancy); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [bdId]);
  if (!ledger || !windows) return null;
  const held = BigInt(ledger.upgradeEscrowAtomic);
  const parked = BigInt(ledger.reserveAtomic);
  if (held === 0n && parked === 0n) return null;
  const last = ledger.dormancy?.lastActivityAt ? Date.parse(ledger.dormancy.lastActivityAt) : NaN;
  const since = ledger.dormancy?.dormantSince ? Date.parse(ledger.dormancy.dormantSince) : NaN;
  const now = Date.now();
  const sweepAt = Number.isFinite(since) ? since : last + windows.dormancySeconds * 1000;
  const forfeitAt = (Number.isFinite(since) ? since : last + windows.dormancySeconds * 1000) + windows.reclaimSeconds * 1000;
  if (!Number.isFinite(forfeitAt)) return null;
  const amount = formatToken(atomicToDecimal((held + parked).toString()));
  const urgent = now >= sweepAt;
  // Owner: a reminder also shortly BEFORE the dormancy threshold (last 30 days, or the last tenth of a short test period).
  const preWindowMs = Math.min(30 * 86_400_000, (windows.dormancySeconds * 1000) / 10);
  const approaching = !urgent && now >= sweepAt - preWindowMs;
  // Quarter of the reclaim period we are in (0..3): the popup comes back at every new quarter.
  const quarterMs = Math.max(1, (windows.reclaimSeconds * 1000) / 4);
  const quarter = urgent ? Math.min(3, Math.floor((now - sweepAt) / quarterMs)) : 0;
  const dismissKey = `dormancy-popup-${bdId}-${Math.round(sweepAt / 1000)}-${urgent ? `q${quarter}` : 'pre'}`;
  let alreadyDismissed = dismissed;
  try { alreadyDismissed = alreadyDismissed || localStorage.getItem(dismissKey) === '1'; } catch { /* ignore */ }
  const dismiss = () => { setDismissed(true); setForceOpen(false); try { localStorage.setItem(dismissKey, '1'); } catch { /* ignore */ } };
  return (
    <>
      <section className={`panel dormancy-reminder${urgent ? ' urgent' : ''}`}>
        <div className="panel-heading"><div><span className="eyebrow">{urgent ? 'ACTION NEEDED · DORMANCY' : 'P3 HOLDING WAITING FOR P4'}</span><h2>{parked > 0n ? `Your holding of ${amount} is parked in the Treasury Reserve` : `Your P3 holding of ${amount} is waiting for P4`}</h2></div>{urgent ? <AlertTriangle size={18} /> : <Hourglass size={18} />}</div>
        <p className="empty-copy">
          {parked > 0n
            ? `It comes back automatically the moment you add a new direct or your P4 fills - before ${formatDate(new Date(forfeitAt).toISOString())}. After that date it is forfeited to the treasury and P3 becomes blank again (rule 21 / 34).`
            : `Add a new direct or let your P4 fill before ${formatDate(new Date(sweepAt).toISOString())} and nothing changes. With no activity it is parked on that date and forfeited on ${formatDate(new Date(forfeitAt).toISOString())}; then P3 is blank again and you refill it by working.`}
        </p>
        <Link to="/join" className="button secondary small">Add a direct now</Link>{' '}
        <button type="button" className="button ghost small" onClick={() => setForceOpen(true)}>Show reminder</button>
      </section>
      {forceOpen || ((urgent || approaching) && !alreadyDismissed) ? createPortal(
        <div className="modal-backdrop" role="dialog" aria-modal="true" style={{ zIndex: 1000 }}>
          <div className="modal-card provider-modal">
            <span className="eyebrow">DORMANCY REMINDER{urgent ? ` · QUARTER ${quarter + 1} OF 4` : ' · BEFORE THE THRESHOLD'}</span>
            <h2>{parked > 0n ? 'Your holding is parked - reclaim it' : urgent ? 'Your P3 holding is about to be parked' : `Your P3 holding is parked on ${formatDate(new Date(sweepAt).toISOString())} unless you act`}</h2>
            <p>{amount} is held for your next-slot upgrade (P3 waiting for P4). {parked > 0n ? 'The dormancy period has passed and it sits in the Treasury Reserve.' : 'The dormancy period has passed.'} Add one new direct, or let your P4 fill, before <strong>{formatDate(new Date(forfeitAt).toISOString())}</strong> - otherwise it is forfeited to the treasury and P3 is blank again.</p>
            <Link to="/join" className="button primary full" onClick={dismiss}>Add a direct</Link>
            <button type="button" className="button ghost full" onClick={dismiss}>Remind me later</button>
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
